import React, { useState, useEffect, useRef } from 'react';

// Animated counter component using requestAnimationFrame and IntersectionObserver
const AnimatedCounter = ({ target, duration = 2000, decimals = 0, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only animate once
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const start = 0;
    const end = parseFloat(target);
    if (isNaN(end)) return;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const progressPercentage = Math.min(progress / duration, 1);
      
      const easeOutQuad = (t) => t * (2 - t);
      const easedProgress = easeOutQuad(progressPercentage);

      const currentValue = start + easedProgress * (end - start);
      setCount(currentValue);

      if (progress < duration) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [target, duration, isVisible]);

  const formattedCount = count.toFixed(decimals);
  const formattedString = decimals === 0 
    ? parseInt(formattedCount, 10).toLocaleString() 
    : formattedCount;

  return <span ref={ref}>{formattedString}{suffix}</span>;
};

function AwardsRecognitions() {
  return (
    <section className="awards-recognitions-section">
      <div className="container awards-recognitions-container">
        {/* Left Side: Title & Description */}
        <div className="awards-left">
          <h2 className="awards-title">Awards & Recognitions</h2>
          <p className="awards-description">
            At Experts Communication, we believe that our true reward lies in the satisfaction of our clients. With a consistent track record of delivering high-quality BPO and IT solutions, we've earned the trust of businesses worldwide. Our dedication to excellence is reflected in the 4.8-star reviews we've proudly received from our clients.
          </p>
        </div>

        {/* Right Side: Trustpilot & Clutch Stats */}
        <div className="awards-right">
          <div className="ratings-flex-row">
            {/* Trustpilot Rating Block */}
            <div className="rating-block">
              <span className="trustpilot-tag">65+ reviews on Trustpilot</span>
              <div className="rating-number">
                <AnimatedCounter target={4.8} decimals={1} suffix="+" />
              </div>
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <svg 
                    key={i} 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="#f59e0b" 
                    stroke="#f59e0b" 
                    strokeWidth="1" 
                    className="star-icon"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                ))}
              </div>
            </div>

            {/* Clutch Rating Block */}
            <div className="rating-block">
              <span className="trustpilot-tag">50+ reviews on Clutch</span>
              <div className="rating-number">
                <AnimatedCounter target={4.9} decimals={1} suffix="+" />
              </div>
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <svg 
                    key={i} 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="#f59e0b" 
                    stroke="#f59e0b" 
                    strokeWidth="1" 
                    className="star-icon"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AwardsRecognitions;
